import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/context/I18nContext";
import { ClientLogo } from "@/components/ClientLogos";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const SECTOR_KEYS = ["all", "energy", "aviation", "government", "finance", "industrial", "mega"];

export default function Clients() {
  const { t, dir } = useI18n();
  const isRtl = dir === "rtl";
  const directory = t.clients.directory;
  const [activeSector, setActiveSector] = useState("all");
  const [openClient, setOpenClient] = useState(null);

  const filtered = useMemo(() => {
    if (activeSector === "all") return directory;
    return directory.filter((c) => c.sector === activeSector);
  }, [activeSector, directory]);

  // Marquee runs across the full directory regardless of filter
  const marquee = [...directory, ...directory];

  return (
    <section
      id="clients"
      className="relative bg-background border-y border-border overflow-hidden"
      data-testid="clients-section"
    >
      {/* Ambient gold blobs */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-primary/10 blur-3xl" />

      <div className="container-page pt-24 md:pt-28 pb-12 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="overline" data-testid="clients-eyebrow">{t.clients.eyebrow}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.05]">
              {t.clients.heading1} <br className="hidden sm:block" />
              <span className="text-primary">{t.clients.heading1Accent}</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 text-muted-foreground leading-relaxed"
            data-testid="clients-description"
          >
            {t.clients.description}
          </motion.p>
        </div>

        {/* Stats strip */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
          {t.clients.stats.map((s, i) => (
            <StatCell key={i} stat={s} index={i} isRtl={isRtl} />
          ))}
        </div>
      </div>

      {/* Marquee strip */}
      <div className="relative py-6 border-y border-border bg-secondary/40">
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-background to-transparent" />
        <div className="flex marquee-track gap-4 w-max" data-testid="clients-marquee">
          {marquee.map((c, i) => (
            <MarqueeBadge key={`m-${i}`} client={c} onClick={() => setOpenClient(c)} />
          ))}
        </div>
      </div>

      {/* Sector filters + grid */}
      <div className="container-page pt-12 pb-24">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="clients-filters">
          {SECTOR_KEYS.map((key) => {
            const label =
              key === "all"
                ? t.clients.filterAll
                : t.clients[`filter${key.charAt(0).toUpperCase() + key.slice(1)}`];
            const active = activeSector === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveSector(key)}
                data-testid={`clients-filter-${key}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-all duration-300 ${
                  active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-transparent text-muted-foreground border-border hover:border-primary hover:text-foreground"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        <motion.div
          layout
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
          data-testid="clients-grid"
        >
          <AnimatePresence mode="popLayout">
            {filtered.map((client, i) => (
              <ClientCard
                key={client.id}
                client={client}
                index={i}
                onOpen={() => setOpenClient(client)}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <ClientModal client={openClient} onClose={() => setOpenClient(null)} t={t} />
    </section>
  );
}

/* ───────────────── Stat counter ───────────────── */

function StatCell({ stat, index, isRtl }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="bg-background p-6 md:p-8 group hover:bg-secondary/60 transition-colors"
      data-testid={`clients-stat-${index}`}
    >
      <div className="text-3xl md:text-4xl lg:text-5xl font-black text-primary tabular-nums tracking-tight">
        {stat.value}
      </div>
      <div className={`mt-2 text-xs uppercase tracking-[0.2em] text-muted-foreground ${isRtl ? "" : ""}`}>
        {stat.label}
      </div>
      <div className="mt-4 h-px w-10 bg-primary/40 group-hover:w-16 transition-all duration-500" />
    </motion.div>
  );
}

/* ───────────────── Marquee badge ───────────────── */

function MarqueeBadge({ client, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 group flex items-center gap-3 px-4 py-2 bg-card border border-border hover:border-primary transition-all duration-300 min-w-[200px]"
      data-testid={`clients-marquee-badge-${client.id}`}
    >
      <div
        className="grid place-items-center w-16 h-10 shrink-0 rounded-sm"
        style={{ backgroundColor: client.accent }}
      >
        <ClientLogo id={client.id} className="w-14 h-8" />
      </div>
      <span className="text-sm font-semibold text-foreground/90 group-hover:text-primary transition-colors whitespace-nowrap">
        {client.name}
      </span>
    </button>
  );
}

/* ───────────────── Client grid card ───────────────── */

function ClientCard({ client, index, onOpen }) {
  return (
    <motion.button
      layout
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.98 }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.04, 0.4) }}
      whileHover={{ y: -6 }}
      className="group relative aspect-[4/5] sm:aspect-[3/4] overflow-hidden border border-border bg-card text-start"
      data-testid={`clients-card-${client.id}`}
    >
      {/* Color swash */}
      <div
        className="absolute inset-0 opacity-90 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background: `linear-gradient(135deg, ${client.accent} 0%, #000 130%)`,
        }}
      />
      {/* Grain noise */}
      <div
        className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
        }}
      />
      {/* Mark */}
      <div className="absolute inset-0 grid place-items-center p-6">
        <ClientLogo id={client.id} className="w-[80%] h-[40%] max-h-24 drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]" />
      </div>
      {/* Bottom info bar */}
      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/50 to-transparent">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.18em] text-primary/90 font-semibold truncate">
              {client.location}
            </div>
            <div className="mt-1 text-sm sm:text-base font-bold text-white leading-tight line-clamp-2">
              {client.name}
            </div>
          </div>
          <div className="shrink-0 w-7 h-7 grid place-items-center bg-primary text-primary-foreground translate-y-1 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
            <ArrowIcon />
          </div>
        </div>
      </div>
      {/* Top corner pill */}
      <div className="absolute top-3 left-3 px-2 py-1 text-[10px] uppercase tracking-[0.15em] bg-black/45 backdrop-blur text-white border border-white/20">
        {client.since}
      </div>
    </motion.button>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}

/* ───────────────── Detail modal ───────────────── */

function ClientModal({ client, onClose, t }) {
  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl bg-background border border-border p-0 overflow-hidden"
        data-testid="client-modal"
      >
        {client && (
          <div className="grid md:grid-cols-5">
            {/* Left visual */}
            <div
              className="relative md:col-span-2 min-h-[200px] md:min-h-[420px] overflow-hidden"
              style={{
                background: `linear-gradient(160deg, ${client.accent} 0%, #000 130%)`,
              }}
            >
              <div
                className="absolute inset-0 opacity-[0.22] mix-blend-overlay"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                }}
              />
              <div className="absolute inset-0 grid place-items-center p-10">
                <ClientLogo id={client.id} className="w-[80%] h-[50%] max-h-32 drop-shadow-[0_4px_30px_rgba(0,0,0,0.5)]" />
              </div>
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                <span className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] bg-black/45 backdrop-blur text-white border border-white/20">
                  {client.since}
                </span>
                <span className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] bg-primary text-primary-foreground font-semibold">
                  {t.clients[`status${client.status.charAt(0).toUpperCase() + client.status.slice(1)}`]}
                </span>
              </div>
            </div>

            {/* Right content */}
            <div className="md:col-span-3 p-6 md:p-8">
              <DialogHeader>
                <DialogTitle className="text-2xl md:text-3xl font-black text-foreground leading-tight">
                  {client.name}
                </DialogTitle>
                <DialogDescription className="mt-1 text-xs uppercase tracking-[0.2em] text-primary">
                  {t.clients[`filter${client.sector.charAt(0).toUpperCase() + client.sector.slice(1)}`]}
                </DialogDescription>
              </DialogHeader>

              <p className="mt-5 text-sm md:text-base text-muted-foreground leading-relaxed">
                {client.summary}
              </p>

              <dl className="mt-6 grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                <div>
                  <dt className="overline text-muted-foreground">{t.clients.labelLocation}</dt>
                  <dd className="mt-1 text-foreground">{client.location}</dd>
                </div>
                <div>
                  <dt className="overline text-muted-foreground">{t.clients.labelSince}</dt>
                  <dd className="mt-1 text-foreground">{client.since}</dd>
                </div>
              </dl>

              <div className="mt-6">
                <div className="overline text-muted-foreground mb-3">{t.clients.labelScope}</div>
                <div className="flex flex-wrap gap-2">
                  {client.scope.map((s) => (
                    <span
                      key={s}
                      className="px-3 py-1.5 text-xs border border-border bg-secondary/60 text-foreground/90"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="mt-8 w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 bg-primary text-primary-foreground text-sm font-semibold uppercase tracking-[0.18em] hover:bg-primary/90 transition-colors"
                data-testid="client-modal-close"
              >
                {t.clients.closeLabel}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
